import { Box } from "@mui/material";
import { ReactNode } from "react";
// FIXME: can be removed once this is copied to storybook styles
import "../stylesheet.scss";

/**
 * A print page-break unit. When `printHeader` is given, the content is wrapped
 * in a table whose <thead> holds the header — browsers repeat a table head on
 * every printed page the table spans, which is what puts the running header on
 * a section's SECOND page and beyond (a plain block header only appears at the
 * defined breaks). On screen the table elements are flattened to blocks and
 * the head is hidden — see `.print-page-table` in stylesheet.scss.
 */
export default function PrintPageWrapper({
  children,
  id,
  printHeader,
  firstPrinted = false,
}: {
  children: ReactNode;
  id?: string;
  printHeader?: ReactNode;
  /** The first page that prints, after web-only siblings (the masthead): no forced break before it, or the
   * printout opens on a blank sheet */
  firstPrinted?: boolean;
}) {
  return (
    <Box
      id={id}
      className="PrintPageWrapper"
      sx={{
        // Dual-declared page breaks: Chrome occasionally drops a lone
        // break-after when the preceding fragment ends near the page edge,
        // which let two wrappers share a page and their content interleave
        // with the running header (the "muddy header" bug, 2026-09-15).
        "&:not(:last-child)": {
          pageBreakAfter: "always",
          // On screen the pages ran together — breathing room between them
          // (print is unaffected: the page break governs there).
          "@media screen": { marginBottom: 7 },
        },
        "&:not(:first-of-type)": {
          "@media print": { breakBefore: firstPrinted ? "auto" : "page" },
        },
        scrollMarginTop: 2,
      }}
    >
      {printHeader ? (
        <table className="print-page-table">
          <thead className="print-page-head">
            <tr>
              <td>{printHeader}</td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                {children}
                {/* Print-only empty tail (stylesheet.scss "page tail"): a last line or row pushed onto a
                    new page is never the cell's last content */}
                <div className="print-page-tail" aria-hidden />
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        children
      )}
    </Box>
  );
}
